// App.js

import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { collection, getDocs } from 'firebase/firestore';
import { analytics, db } from './utils/firebaseConfig';
import { logEvent } from 'firebase/analytics';

import Container           from '@mui/material/Container';
import Grid                from '@mui/material/Grid';
import useMediaQuery       from '@mui/material/useMediaQuery';
import { useTheme }        from '@mui/material/styles';

import Navbar              from './components/Navbar';
import Sidebar             from './components/Sidebar';
import Footer              from './components/Footer';
import HomePage            from './components/HomePage';
import LoginPage           from './components/LoginPage';
import SignUpPage          from './components/SignUpPage';
import ForgotPasswordPage  from './components/ForgotPasswordPage';
import InfoPage            from './components/InfoPage';
import About               from './components/About';
import Bulletin            from './components/Bulletin';
import Trending            from './components/Trending';
import CategoryPosts       from './components/CategoryPosts';
import TitleManager        from './components/TitleManager';
import { GlobalStyle }     from './GlobalStyles';
import { ProtectedRoute }  from './components/ProtectedRoute';

/* ─────────── NEW ‑ location based feeds ─────────── */
import CityPosts           from './components/CityPosts';
import StatePosts          from './components/StatePosts';
import CountryPosts        from './components/CountryPosts';
import LocationPosts       from './components/LocationPosts';

import { AuthProvider }    from './contexts/AuthContext';
import ActiveItemContext   from './contexts/ActiveItemContext';
import UsernamesContext    from './contexts/UsernamesContext';
import { UsersProvider }   from './contexts/UsersContext';

/* ─────────── LOOKS stack ─────────── */
import LooksPostPage       from './components/LooksPostPage';
import LooksUsersPosts     from './components/LooksUsersPosts';
import LooksPostForm       from './components/LooksPostForm';
import LooksEditPostForm   from './components/LooksEditPostForm';
import { LooksPostsProvider } from './contexts/LooksPostsContext';

/* ─────────── LOOKS likes ─────────── */
import { LooksLikesProvider } from './contexts/LooksLikesContext';
import LooksLikesPage      from './components/LooksLikesPage';

/* ─────────── LOOKS reports ─────────── */
import { LooksReportsProvider } from './contexts/LooksReportsContext';

/* ─────────── LOOKS search ─────────── */
import LooksSearchResultsPage from './components/LooksSearchResultsPage';

/* ─────────── LOOKS my‑posts ─────────── */
import LooksMyPostsPage    from './components/LooksMyPostsPage';

/* ─────────── LOOKS polls ─────────── */
import LooksPolls          from './components/LooksPolls';

/* ─────────── NEW‑POSTS FEED ─────────── */
import NewPosts            from './components/NewPosts';

/* ─────────── LOOKS admin dashboard ─────────── */
import LooksAdminDashboard from './components/LooksAdminDashboard';

/* ─────────── NEW: contact form ─────────── */
import LooksContactForm    from './components/LooksContactForm';

/* ─────────── NEW: BRIEF BIOS ─────────── */
import BriefBioForm        from './components/BriefBioForm';
import BriefBio            from './components/BriefBio';
import BriefBioPage        from './components/BriefBioPage';
import BriefBioRando       from './components/BriefBioRando'; // ✅ NEW IMPORT

function PageViewTracker() {
  const location = useLocation();
  useEffect(() => {
    logEvent(analytics, 'page_view', {
      page_path : location.pathname,
      page_title: document.title,
    });
  }, [location]);
  return null;
}

function App() {
  const [activeItem, setActiveItem] = useState('Home');
  const [usernames,  setUsernames]  = useState({});
  const [isSidebarVisible, setIsSidebarVisible] = useState(false);

  const theme         = useTheme();
  const isLargeScreen = useMediaQuery(theme.breakpoints.up('lg'));

  useEffect(() => {
    const fetchAndSyncUsers = async () => {
      try {
        const usersCol     = collection(db, 'users');
        const userSnapshot = await getDocs(usersCol);
        const users        = userSnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
        setUsernames(
          users.reduce((acc, curr) => ({ ...acc, [curr.id]: curr.username }), {})
        );
      } catch (error) {
        console.error('Error fetching users from Firestore:', error);
      }
    };
    fetchAndSyncUsers();
  }, []);

  useEffect(() => {
    const handleResize = () => setIsSidebarVisible(window.innerWidth > 768);
    window.addEventListener('resize', handleResize);
    handleResize();
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <>
      <GlobalStyle />
      <AuthProvider>
        <LooksReportsProvider>
          <LooksPostsProvider>
            <LooksLikesProvider>
              <UsersProvider>
                <UsernamesContext.Provider value={{ usernames, setUsernames }}>
                  <ActiveItemContext.Provider value={{ activeItem, setActiveItem }}>
                    <Router>
                      <TitleManager />
                      <PageViewTracker />

                      <div className="App">
                        <Navbar
                          isSidebarVisible={isSidebarVisible}
                          setIsSidebarVisible={setIsSidebarVisible}
                        />

                        <Container maxWidth="lg">
                          <Grid container spacing={3}>
                            <Grid item xs={12} md={8} lg={isLargeScreen ? 8 : 9}>
                              <Routes>
                                {/* ---------- CORE PAGES ---------- */}
                                <Route path="/"               element={<HomePage />}       />
                                <Route path="/info"           element={<InfoPage />}       />
                                <Route path="/about"          element={<About />}          />
                                <Route path="/categoryposts/:category" element={<CategoryPosts />} />

                                {/* ▼▼▼ NEW LOCATION FEEDS ▼▼▼ */}
                                <Route path="/cityposts/:city"           element={<CityPosts />} />
                                <Route path="/stateposts/:state"         element={<StatePosts />} />
                                <Route path="/countryposts/:country"     element={<CountryPosts />} />
                                <Route path="/locationposts/:location"   element={<LocationPosts />} />
                                {/* ▲▲▲ NEW LOCATION FEEDS ▲▲▲ */}

                                <Route path="/bulletin"       element={<Bulletin />}       />
                                <Route path="/trending"       element={<Trending />}       />
                                <Route path="/newposts"       element={<NewPosts />}       />

                                {/* ---------- AUTH ---------- */}
                                <Route path="/login"          element={<LoginPage />}      />
                                <Route path="/signup"         element={<SignUpPage />}     />
                                <Route path="/forgot-password" element={<ForgotPasswordPage />} />

                                {/* ---------- ADMIN BULLETIN ---------- */}
                                <Route
                                  path="/admin"
                                  element={
                                    <ProtectedRoute allowedRoles={['admin']}>
                                      <Bulletin />
                                    </ProtectedRoute>
                                  }
                                />

                                {/* ---------- LOOKS ADMIN DASHBOARD ---------- */}
                                <Route
                                  path="/looks-admin-dashboard"
                                  element={
                                    <ProtectedRoute allowedRoles={['admin']}>
                                      <LooksAdminDashboard />
                                    </ProtectedRoute>
                                  }
                                />

                                {/* ---------- CONTACT FORM (new) ---------- */}
                                <Route path="/lookscontactform" element={<LooksContactForm />} />

                                {/* ---------- LOOKS ROUTES ---------- */}
                                <Route path="/looksposts/:id"               element={<LooksPostPage />} />
                                <Route path="/looksusersposts/:username"    element={<LooksUsersPosts />} />
                                <Route
                                  path="/newlookspost"
                                  element={
                                    <ProtectedRoute>
                                      <LooksPostForm />
                                    </ProtectedRoute>
                                  }
                                />
                                <Route
                                  path="/editlookspost/:id"
                                  element={
                                    <ProtectedRoute>
                                      <LooksEditPostForm />
                                    </ProtectedRoute>
                                  }
                                />
                                <Route
                                  path="/looksposts/edit/:id"
                                  element={
                                    <ProtectedRoute>
                                      <LooksEditPostForm />
                                    </ProtectedRoute>
                                  }
                                />

                                {/* ---------- LOOKS LIKES ---------- */}
                                <Route
                                  path="/lookslikes"
                                  element={
                                    <ProtectedRoute>
                                      <LooksLikesPage />
                                    </ProtectedRoute>
                                  }
                                />

                                {/* ---------- LOOKS MY POSTS ---------- */}
                                <Route
                                  path="/looksmyposts"
                                  element={
                                    <ProtectedRoute>
                                      <LooksMyPostsPage />
                                    </ProtectedRoute>
                                  }
                                />

                                {/* ---------- LOOKS POLLS ---------- */}
                                <Route path="/lookspolls" element={<LooksPolls />} />

                                {/* ---------- LOOKS SEARCH ---------- */}
                                <Route path="/lookssearch" element={<LooksSearchResultsPage />} />

                                {/* ---------- BRIEF BIOS ---------- */}
                                <Route
                                  path="/briefbioform"
                                  element={
                                    <ProtectedRoute allowedRoles={['admin']}>
                                      <BriefBioForm />
                                    </ProtectedRoute>
                                  }
                                />
                                <Route
                                  path="/briefbios"
                                  element={
                                    <ProtectedRoute allowedRoles={['admin']}>
                                      <BriefBio />
                                    </ProtectedRoute>
                                  }
                                />
                                <Route
                                  path="/briefbios/:id"
                                  element={
                                    <ProtectedRoute allowedRoles={['admin']}>
                                      <BriefBioPage />
                                    </ProtectedRoute>
                                  }
                                />
                                <Route path="/briefbiorando" element={<BriefBioRando />} />
                              </Routes>
                            </Grid>

                            {isLargeScreen && <Grid item lg={1} />}
                            <Grid item xs={12} md={4} lg={3}>
                              <Sidebar
                                isVisible={isSidebarVisible}
                                setIsSidebarVisible={setIsSidebarVisible}
                              />
                            </Grid>
                          </Grid>
                        </Container>

                        <Footer />
                      </div>
                    </Router>
                  </ActiveItemContext.Provider>
                </UsernamesContext.Provider>
              </UsersProvider>
            </LooksLikesProvider>
          </LooksPostsProvider>
        </LooksReportsProvider>
      </AuthProvider>
    </>
  );
}

export default App;
